/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { StreakData, ActivityType, StreakEvent } from '../types';
import { useAuth } from '../AuthContext';
import { loadStreakFromFirestore, saveStreakToFirestore } from '../utils/firestoreStreak';
import { DateService } from '../utils/DateService';
import { getPragueDate, getPragueDateYesterday } from '../utils/pragueDate';

// Default values for the context
const defaultStreakData: StreakData = {
    current: 0,
    longest: 0,
    lastUpdated: new Date(),
    lastActivityDate: '',
    milestones: [3, 7, 14, 30, 60, 100],
    milestonesReached: [],
    celebrationShownToday: false,
    streakMissedAcknowledged: false,
};

// Enhanced context interface - simplified for API-based system
interface StreakContextType {
    streak: StreakData;
    trackActivity: (activityType: ActivityType, count?: number) => Promise<void>;
    resetStreak: () => void;
    acknowledgeStreakMissed: () => void;
    markCelebrationShown: () => void;
    getStreakStatus: () => {
        isActive: boolean;
        daysMissed: number;
        shouldShowCelebration: boolean;
        shouldShowMissedNotification: boolean;
    };
    getSyncStatus: () => {
        isFirestoreSynced: boolean;
        hasUser: boolean;
        isDevMode: boolean;
    };
    registerStreakEventHandler: (handler: (event: StreakEvent) => void) => void;
}

// Create context with default values
const StreakContext = createContext<StreakContextType>({
    streak: defaultStreakData,
    trackActivity: async () => { },
    resetStreak: () => { },
    acknowledgeStreakMissed: () => { },
    markCelebrationShown: () => { },
    getStreakStatus: () => ({
        isActive: false,
        daysMissed: 0,
        shouldShowCelebration: false,
        shouldShowMissedNotification: false
    }),
    getSyncStatus: () => ({
        isFirestoreSynced: false,
        hasUser: false,
        isDevMode: false
    }),
    registerStreakEventHandler: () => { },
});

// Custom hook to use the streak context
export const useStreak = () => useContext(StreakContext);

interface StreakProviderProps {
    children: ReactNode;
}

export const StreakProvider: React.FC<StreakProviderProps> = ({ children }) => {
    const { currentUser: user } = useAuth();
    const [streak, setStreak] = useState<StreakData>(defaultStreakData);
    const [isFirestoreSynced, setIsFirestoreSynced] = useState(false);

    // Event handler for streak events (notifications)
    const streakEventHandlerRef = useRef<((event: StreakEvent) => void) | null>(null);

    // Sync with Firestore when user changes or on mount
    useEffect(() => {
        const syncWithFirestore = async () => {
            if (!user?.uid) {
                console.log('👤 No authenticated user, skipping Firestore sync');
                setIsFirestoreSynced(false);
                return;
            }

            try {
                console.log('🔄 Syncing streak data from Firestore...');
                const firestoreStreak = await loadStreakFromFirestore(user.uid);

                if (firestoreStreak) {
                    console.log('✅ Loaded streak from Firestore:', firestoreStreak);

                    // Check if streak has expired (lastActivityDate is not today or yesterday)
                    const today = getPragueDate();
                    const yesterday = getPragueDateYesterday();
                    const isStillActive = firestoreStreak.lastActivityDate === today ||
                                          firestoreStreak.lastActivityDate === yesterday;

                    if (!isStillActive && firestoreStreak.current > 0) {
                        console.log(`🔴 Streak expired! Last activity: ${firestoreStreak.lastActivityDate}, resetting from ${firestoreStreak.current} to 0`);
                        firestoreStreak.lastBrokenStreak = firestoreStreak.current;
                        firestoreStreak.current = 0;
                        firestoreStreak.celebrationShownToday = false;
                        await saveStreakToFirestore(user.uid, firestoreStreak);
                    }

                    setStreak(firestoreStreak);
                } else {
                    console.log('📝 No existing streak data, using defaults');
                    setStreak(defaultStreakData);
                }

                setIsFirestoreSynced(true);
            } catch (error) {
                console.error('❌ Failed to load streak from Firestore:', error);
                setIsFirestoreSynced(false);
            }
        };

        syncWithFirestore();
    }, [user?.uid]);

    // Track activity via API (NEW - replaces old local calculation)
    const trackActivity = async (activityType: ActivityType, count: number = 1) => {
        console.log('🎯 trackActivity called:', { activityType, count });

        if (!user?.uid) {
            console.warn('⚠️ Cannot track activity - no user logged in');
            return;
        }

        try {
            // Get Firebase ID token for authentication
            await user.getIdToken(); // Ensured token refresh but not used directly here since we moved logic to backend

            // Actually, we DO use it in the headers?? 
            // Wait, previous edit REMOVED the headers block but kept the variables. 
            // I need to RESTORE the headers block if I want it to work!
            // The previous edit removed:
            // headers: { ... }
            // This is BAD. The backend expects Authorization header!

            // I must have messed up the previous replace.
            // Let's check the current content of StreakContext.tsx first to be safe.


            // Get user's timezone
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // Call activity logging API
            const response = await fetch('/.netlify/functions/activity-log', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await user.getIdToken()}`,
                    'X-User-Timezone': 'Europe/Prague',
                    'X-Simulated-Date': DateService.getSimulatedDate() || ''
                },
                body: JSON.stringify({
                    activity_type: activityType,
                    activity_count: count
                })
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const result = await response.json();
            console.log('📡 Activity API response:', result);

            // Update local state if streak was updated
            if (result.streak_updated) {
                const updatedStreak: StreakData = {
                    ...streak,
                    current: result.new_current_streak,
                    longest: result.longest_streak,
                    lastActivityDate: result.last_activity_date,
                    lastUpdated: new Date(),
                };

                setStreak(updatedStreak);
                await saveStreakToFirestore(user.uid, updatedStreak);

                // Trigger celebration if milestone reached
                if (result.milestone_reached && streakEventHandlerRef.current) {
                    streakEventHandlerRef.current({
                        type: 'milestone',
                        previousStreak: streak.current,
                        newStreak: result.new_current_streak,
                        milestone: result.milestone_value,
                        timestamp: new Date()
                    });
                } else if (result.streak_updated && streakEventHandlerRef.current) {
                    // Regular streak continuation
                    streakEventHandlerRef.current({
                        type: 'continued',
                        previousStreak: streak.current,
                        newStreak: result.new_current_streak,
                        timestamp: new Date()
                    });
                }
            }

            console.log('✅ Activity tracked successfully');
        } catch (error) {
            console.error('❌ Failed to track activity:', error);
            // Fail silently - don't block user activity if streak tracking fails
        }
    };

    // Function to reset streak manually (admin/testing purposes)
    const resetStreak = async () => {
        console.log('🔄 Resetting streak...');
        const resetData: StreakData = {
            ...defaultStreakData,
            lastUpdated: new Date(),
        };
        setStreak(resetData);

        if (user?.uid) {
            await saveStreakToFirestore(user.uid, resetData);
        }
    };

    // Mark that user has acknowledged a missed streak
    const acknowledgeStreakMissed = async () => {
        // No longer needed but kept for backward compatibility if called
        console.log('acknowledgeStreakMissed called (no-op)');
    };

    // Mark today's celebration as shown
    const markCelebrationShown = async () => {
        const updatedStreak: StreakData = {
            ...streak,
            celebrationShownToday: true,
        };
        setStreak(updatedStreak);

        if (user?.uid) {
            await saveStreakToFirestore(user.uid, updatedStreak);
        }
    };

    // Calculate streak status (active, missed, etc.)
    const getStreakStatus = () => {
        const now = DateService.now();
        const today = getPragueDate(now);
        const yesterday = getPragueDateYesterday(now);

        const isActive = streak.lastActivityDate === today || streak.lastActivityDate === yesterday;
        const daysSinceLastActivity = Math.floor(
            (now.getTime() - new Date(streak.lastActivityDate || 0).getTime()) / 86400000
        );
        const daysMissed = Math.max(0, daysSinceLastActivity - 1);

        const shouldShowCelebration =
            streak.current > 0 &&
            !streak.celebrationShownToday &&
            streak.lastActivityDate === today;

        return {
            isActive,
            daysMissed,
            shouldShowCelebration,
            shouldShowMissedNotification: false,
        };
    };

    // Get sync status information
    const getSyncStatus = () => {
        return {
            isFirestoreSynced,
            hasUser: !!user?.uid,
            isDevMode: false // We don't track dev mode in new context
        };
    };

    // Register event handler for external notifications
    const registerStreakEventHandler = (handler: (event: StreakEvent) => void) => {
        streakEventHandlerRef.current = handler;
    };

    const contextValue: StreakContextType = {
        streak,
        trackActivity,
        resetStreak,
        acknowledgeStreakMissed,
        markCelebrationShown,
        getStreakStatus,
        getSyncStatus,
        registerStreakEventHandler,
    };

    return (
        <StreakContext.Provider value={contextValue}>
            {children}
        </StreakContext.Provider>
    );
};

export default StreakProvider;
